import { useCallback, useEffect, useState } from "react";
import { ApiError, describeError } from "../../api/client";
import type { AdminContactPage } from "../../api/types";
import { RequireRole } from "../../auth/RequireRole";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Pagination } from "../../components/Pagination";
import { listContacts } from "../contacts/api";
import { addContactsToSegment } from "./api";

type SearchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; page: AdminContactPage };

export function ManualContactInclusionPanel({
  segmentId,
  onAdded,
}: {
  segmentId: string;
  onAdded: () => Promise<void> | void;
}) {
  return (
    <RequireRole role="Operador">
      <ManualContactInclusionForm segmentId={segmentId} onAdded={onAdded} />
    </RequireRole>
  );
}

function ManualContactInclusionForm({
  segmentId,
  onAdded,
}: {
  segmentId: string;
  onAdded: () => Promise<void> | void;
}) {
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [searchState, setSearchState] = useState<SearchState>({ status: "loading" });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    setSearchState({ status: "loading" });
    try {
      const result = await listContacts({
        q: activeQuery || undefined,
        estado: "activo",
        page,
        size,
      });
      setSearchState({ status: "ready", page: result });
    } catch (error) {
      setSearchState({
        status: "error",
        message: describeError(error, "No se pudieron cargar los contactos."),
      });
    }
  }, [activeQuery, page, size]);

  useEffect(() => {
    void fetchContacts();
  }, [fetchContacts]);

  const toggleContact = (contactId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
    setActionError(null);
    setSuccessMessage(null);
  };

  const submit = async () => {
    if (selectedIds.size === 0) return;

    setSubmitting(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      const result = await addContactsToSegment(segmentId, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSuccessMessage(
        `${result.added} contactos agregados${result.alreadyIncluded > 0 ? `; ${result.alreadyIncluded} ya estaban incluidos` : ""}. Miembros resueltos: ${result.memberCount}.`,
      );
      await onAdded();
    } catch (error) {
      const message =
        error instanceof ApiError && error.kind === "validation"
          ? describeError(error, "La selección de contactos no es válida.")
          : error instanceof ApiError && error.kind === "notFound"
            ? "El segmento o uno de los contactos ya no existe. Actualice la búsqueda e intente nuevamente."
            : describeError(error, "No se pudieron agregar los contactos al segmento.");
      setActionError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section aria-labelledby={`manual-contacts-${segmentId}`} className="v2-segment-contact-inclusion">
      <h2 id={`manual-contacts-${segmentId}`}>Agregar contactos manualmente</h2>
      <p>La inclusión manual no modifica los datos del contacto ni los criterios del segmento.</p>

      <div>
        <form
          className="v2-segment-contact-inclusion__search"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setActiveQuery(query.trim());
          }}
        >
          <label htmlFor={`segment-contact-query-${segmentId}`}>Buscar por email, nombre o empresa</label>
          <input
            id={`segment-contact-query-${segmentId}`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button type="submit" className="v2-u-target-min">
            Buscar
          </button>
        </form>

        {searchState.status === "loading" && <p role="status">Cargando contactos…</p>}
        {searchState.status === "error" && (
          <FeedbackPanel variant="error" message={searchState.message} onRetry={() => void fetchContacts()} />
        )}
        {searchState.status === "ready" && (
          <>
            <p aria-live="polite">{searchState.page.total} contactos activos encontrados</p>
            {searchState.page.items.length === 0 ? (
              <FeedbackPanel variant="empty" message="No hay contactos activos que coincidan con la búsqueda." />
            ) : (
              <>
                <fieldset>
                  <legend>Seleccione uno o más contactos</legend>
                  {searchState.page.items.map((contact) => (
                    <label key={contact.contactId}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contact.contactId)}
                        onChange={() => toggleContact(contact.contactId)}
                      />
                      {contact.nombre} — {contact.email} — {contact.empresa}
                    </label>
                  ))}
                </fieldset>
                <Pagination
                  page={searchState.page.page}
                  size={searchState.page.size}
                  total={searchState.page.total}
                  onPageChange={setPage}
                  onSizeChange={(nextSize) => {
                    setPage(1);
                    setSize(nextSize);
                  }}
                />
              </>
            )}
          </>
        )}

        <p aria-live="polite">{selectedIds.size} contactos seleccionados</p>
        <button
          type="button"
          className="v2-u-target-min"
          disabled={submitting || selectedIds.size === 0}
          onClick={() => void submit()}
        >
          {submitting ? "Agregando…" : "Agregar al segmento"}
        </button>
        {actionError && <p role="alert">{actionError}</p>}
        {successMessage && <p role="status">{successMessage}</p>}
      </div>
    </section>
  );
}
